import * as React from 'react';
import { useSchemes, useSchemeSubscribers } from '../../schemes/network/schemes.api';
import { useExitRequests, useRequestExit } from '../hooks';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { AmountDisplay } from '../../../components/ui/AmountDisplay';
import { Button } from '../../../components/ui/Button';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ForeclosureDetailDrawer } from './ForeclosureDetailDrawer';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { formatMoney } from '@repo/utils';
import { useDrawerParam } from '../../../lib/hooks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/Dialog';

const mapStatusToBadge = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'APPROVED':
    case 'REFUND_INITIATED':
    case 'NOC_ISSUED':
      return 'success';
    case 'UNDER_REVIEW':
    case 'SETTLEMENT_COMPUTED':
      return 'warning';
    case 'REJECTED':
      return 'danger';
    case 'REQUESTED':
    default:
      return 'info';
  }
};

export function ForeclosureList() {
  const [selectedSchemeId, setSelectedSchemeId] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('ALL');
  const { id: selectedRequestId, open: openDrawer, close: closeDrawer } = useDrawerParam('drawer');
  const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
  const [newSubscriberId, setNewSubscriberId] = React.useState('');
  const [newReason, setNewReason] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  // Queries
  const { data: schemesResponse, isLoading: isLoadingSchemes } = useSchemes({ limit: 100 });
  const schemes = schemesResponse?.data ?? [];

  const { data: exits, isLoading: isLoadingExits, refetch: refetchExits } = useExitRequests(selectedSchemeId);
  const { data: subscribersResponse, isLoading: isLoadingSubscribers } = useSchemeSubscribers(selectedSchemeId, { limit: 100 });
  const subscribers = subscribersResponse?.data ?? [];

  const requestExitMutation = useRequestExit();

  // Selected scheme detail
  const currentScheme = React.useMemo(() => {
    return schemes.find((s) => s.id === selectedSchemeId);
  }, [selectedSchemeId, schemes]);

  // Map exit requests with customer names
  const processedExits = React.useMemo(() => {
    if (!exits) return [];
    return exits.map((exit) => {
      const sub = subscribers.find((s) => s.id === exit.subscriberId);
      return {
        ...exit,
        customerName: sub?.customerName || exit.customerName || 'Unknown Customer',
        customerMobile: sub?.customerMobile || exit.customerMobile || '',
        ticketNumbers: sub?.ticketNumbers ?? [],
      };
    });
  }, [exits, subscribers]);

  // Filtered exits
  const filteredExits = React.useMemo(() => {
    if (statusFilter === 'ALL') return processedExits;
    return processedExits.filter((exit) => exit.status === statusFilter);
  }, [processedExits, statusFilter]);

  // Unhappy Path: Prevent requesting foreclosure when they have unresolved pending dues.
  // We can filter subscribers to show warnings or check if they are eligible.
  const eligibleSubscribers = React.useMemo(() => {
    // Show subscribers that haven't exited yet
    return subscribers.filter((sub) => sub.status !== 'EXITED');
  }, [subscribers]);

  const handleOpenRequestDialog = () => {
    setNewSubscriberId('');
    setNewReason('');
    setFormError(null);
    setIsRequestDialogOpen(true);
  };

  const handleCreateRequest = () => {
    if (!newSubscriberId) {
      setFormError('Please select a subscriber.');
      return;
    }
    if (!newReason.trim()) {
      setFormError('Please provide a reason for exit.');
      return;
    }

    const selectedSub = subscribers.find((s) => s.id === newSubscriberId);
    if (selectedSub?.status === 'DEFAULTED') {
      // Allow requesting exit but warn staff, or proceed.
    }

    requestExitMutation.mutate(
      { subscriberId: newSubscriberId, schemeId: selectedSchemeId },
      {
        onSuccess: () => {
          setIsRequestDialogOpen(false);
          refetchExits();
        },
        onError: (err: any) => {
          setFormError(err?.message || 'Failed to submit request.');
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-surface-2 p-4 rounded-xl border border-border">
        <div className="flex-1 max-w-sm">
          <label htmlFor="scheme-select" className="block text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
            Select Scheme / Chit Scheme
          </label>
          <select
            id="scheme-select"
            className="w-full p-2.5 border border-border rounded-lg bg-surface-1 text-sm focus:outline-none focus:border-accent-500"
            value={selectedSchemeId}
            onChange={(e) => {
              setSelectedSchemeId(e.target.value);
              setStatusFilter('ALL');
            }}
          >
            <option value="">-- Choose a Scheme --</option>
            {schemes.map((scheme) => (
              <option key={scheme.id} value={scheme.id}>
                {scheme.schemeNumber} - {scheme.name} ({formatMoney(scheme.chitValuePaise, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })})
              </option>
            ))}
          </select>
        </div>

        {selectedSchemeId && (
          <Button
            onClick={handleOpenRequestDialog}
            className="sm:self-end justify-center bg-danger-text hover:bg-danger-600 border-danger-text text-white"
          >
            Initiate Exit / Foreclosure
          </Button>
        )}
      </div>

      {!selectedSchemeId ? (
        <EmptyState
          title="No Scheme Selected"
          description="Select a scheme from the dropdown above to view, calculate settlement quotes, and process exit requests."
          className="bg-surface-2 border border-border border-dashed rounded-xl py-12"
        />
      ) : (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="flex items-center gap-2 border-b border-border pb-2 overflow-x-auto">
            {['ALL', 'REQUESTED', 'SETTLEMENT_COMPUTED', 'APPROVED', 'REJECTED'].map((filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? 'primary' : 'secondary'}
                onClick={() => setStatusFilter(filter)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors',
                  statusFilter === filter
                    ? ''
                    : 'bg-surface-2 hover:bg-surface-3 text-text-secondary border-none',
                ].join(' ')}
              >
                {filter.replace(/_/g, ' ')}
              </Button>
            ))}
          </div>

          {isLoadingExits ? (
            <div className="flex justify-center items-center py-12 text-sm text-text-muted">
              Loading exit requests…
            </div>
          ) : filteredExits.length === 0 ? (
            <EmptyState
              title="No Exit Requests Found"
              description={`There are no exit requests matching "${statusFilter.replace(/_/g, ' ')}" status for this scheme.`}
              className="bg-surface-2 border border-border border-dashed rounded-xl py-12"
            />
          ) : (
            <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-2 border-b border-border text-xs font-bold text-text-secondary uppercase tracking-wider">
                    <th className="px-6 py-4">Customer Name</th>
                    <th className="px-6 py-4">Ticket</th>
                    <th className="px-6 py-4">Exit Type</th>
                    <th className="px-6 py-4">Request Date</th>
                    <th className="px-6 py-4">Settlement Quote</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {filteredExits.map((exit) => (
                    <tr key={exit.id} className="hover:bg-surface-2/40 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-semibold text-text-primary">{exit.customerName}</div>
                          <div className="text-xs text-text-muted">{exit.customerMobile}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-text-secondary">
                        {exit.ticketNumbers.length > 0 ? `#${exit.ticketNumbers.join(', ')}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-medium px-2 py-0.5 rounded-[4px] bg-surface-3 border border-border text-text-secondary uppercase">
                          {exit.exitType.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-text-muted">
                        {new Date(exit.requestDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 font-bold text-text-primary">
                        {exit.settlementAmount !== null ? (
                          <AmountDisplay paise={exit.settlementAmount} />
                        ) : (
                          <span className="text-text-muted font-normal text-xs italic">Not computed</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          status={mapStatusToBadge(exit.status)}
                          label={exit.status.replace(/_/g, ' ')}
                        />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDrawer(exit.id)}
                          className="text-accent-500 hover:text-accent-600 hover:bg-accent-50 font-bold"
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Initiation Dialog */}
      <Dialog open={isRequestDialogOpen} onOpenChange={setIsRequestDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Initiate Premature Exit</DialogTitle>
            <DialogDescription>
              Submit an exit request for a subscriber in scheme <span className="font-semibold text-text-primary">{currentScheme?.schemeNumber}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="sub-select" className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                Select Subscriber
              </label>
              <select
                id="sub-select"
                className="w-full p-2.5 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:border-accent-500"
                value={newSubscriberId}
                onChange={(e) => {
                  setNewSubscriberId(e.target.value);
                  setFormError(null);
                }}
              >
                <option value="">-- Choose Subscriber --</option>
                {eligibleSubscribers.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.customerName} (Ticket {sub.ticketNumbers.join(', ')} - {sub.status})
                  </option>
                ))}
              </select>
            </div>

            {/* Unhappy Path warning check */}
            {newSubscriberId && subscribers.find((s) => s.id === newSubscriberId)?.status === 'DEFAULTED' && (
              <div className="p-3 bg-warning-bg border border-warning-text/20 rounded-lg text-xs text-warning-text leading-relaxed">
                <span className="font-bold block mb-1">Warning: Outstanding Dues</span>
                This subscriber is currently marked as <span className="font-bold">DEFAULTED</span> and has pending unpaid dues. Foreclosure charges and dues recovery will be processed during settlement computation.
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="reason-input" className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                Reason for Exit
              </label>
              <textarea
                id="reason-input"
                className="w-full h-20 p-2.5 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:border-accent-500"
                placeholder="Specify reason for exit..."
                value={newReason}
                onChange={(e) => {
                  setNewReason(e.target.value);
                  if (e.target.value.trim()) setFormError(null);
                }}
              />
            </div>

            {formError && (
              <p className="text-xs font-semibold text-danger-text bg-danger-bg/5 p-2 rounded">
                {formError}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsRequestDialogOpen(false)}
              disabled={requestExitMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRequest}
              loading={requestExitMutation.isPending}
              className="bg-danger-text hover:bg-danger-600 border-danger-text"
            >
              Submit Exit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ForeclosureDetailDrawer
        isOpen={!!selectedRequestId}
        exitRequestId={selectedRequestId}
        onClose={() => {
          closeDrawer();
          refetchExits();
        }}
        subscribers={subscribers}
        currentScheme={currentScheme ?? null}
      />
    </div>
  );
}
